/**
 * Pure calculation helpers for the Absence dialog summary.
 *
 * Presentation/calculation only: never persist values derived here. Dates are treated as
 * inclusive local-calendar days (From and To both count). Saturday/Sunday are excluded from the
 * generated day count unless explicitly included, matching the backend day-generation contract.
 */

function toLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Counts the generated absence days in an inclusive [from, to] range.
 *
 * - `from == to` yields 1 day (unless that single day is an excluded weekend day).
 * - Saturdays count only when `includeSaturday` is true; Sundays only when `includeSunday` is true.
 * - Returns 0 when either date is missing or when `to` is before `from`.
 */
export function countAbsenceDays(
  from: Date | null | undefined,
  to: Date | null | undefined,
  includeSaturday: boolean,
  includeSunday: boolean
): number {
  if (!from || !to) {
    return 0;
  }

  const cursor = toLocalDay(from);
  const end = toLocalDay(to);
  if (end.getTime() < cursor.getTime()) {
    return 0;
  }

  let days = 0;
  while (cursor.getTime() <= end.getTime()) {
    const weekday = cursor.getDay();
    const isSaturday = weekday === 6;
    const isSunday = weekday === 0;
    const included = (!isSaturday && !isSunday) || (isSaturday && includeSaturday) || (isSunday && includeSunday);
    if (included) {
      days += 1;
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

/**
 * Total hours for the summary: `days * durationHoursPerDay`, rounded to 2 decimals.
 * Returns 0 when either input is non-positive.
 */
export function absenceTotalHours(days: number, durationHoursPerDay: number | null | undefined): number {
  if (!durationHoursPerDay || durationHoursPerDay <= 0 || days <= 0) {
    return 0;
  }

  return Math.round(days * durationHoursPerDay * 100) / 100;
}

/**
 * True when both dates fall on the same local-calendar day.
 */
export function isSameLocalDay(from: Date | null | undefined, to: Date | null | undefined): boolean {
  if (!from || !to) {
    return false;
  }

  return toLocalDay(from).getTime() === toLocalDay(to).getTime();
}
